import * as models from "../../lib/api/models";
import styles from "./AccountSearchPage.module.css";
import StyledButton from "../../modules/common/buttons/StyledButton";
import ValidationMessage from "../../modules/common/ValidationMessage/ValidationMessage";
import { useId, useState } from "react";
import { ValidationErrors } from "../../lib/api/models";
import { UseMutateAsyncFunction } from "@tanstack/react-query";
import { setValidationErrorsAndDisplayGeneralErrors } from "../../lib/validation";
import { getDisplayError, NetworkDisplayError } from "../../lib/api/endpoints";
import { accountSearchRequestSchema } from "../../lib/validation/schemas";
import { ZodError } from "zod";
import classNames from "classnames";

const strings = {
  searchByPhone: "Search by Phone, Account ID, or Account Number",
  searchByPhonePlaceholder: "Phone, Account ID, or Account Number",
  search: "Search",
};

type Props = {
  isDisabled: boolean;
  isLoading: boolean;

  resetAccountSearchQueries: () => void;
  accountSearch: UseMutateAsyncFunction<
    models.AccountSearchResponse,
    unknown,
    models.AccountSearchRequest,
    unknown
  >;
  accountSearchResponse: models.AccountSearchResponse | undefined;
  setDisplayError: React.Dispatch<
    React.SetStateAction<NetworkDisplayError | undefined>
  >;
  setAccountToView: React.Dispatch<
    React.SetStateAction<models.AccountSearchResult | undefined>
  >;
};

export default function SingularSearchForm({
  isDisabled,
  isLoading,
  accountSearch,
  accountSearchResponse,
  resetAccountSearchQueries,
  setDisplayError,
  setAccountToView,
}: Props) {
  const labelId = useId();

  const [search, setSearch] = useState("");
  const [validationMessages, setValidationMessages] =
    useState<ValidationErrors>();

  const onDisplayGeneralErrors = (err: unknown) => {
    setDisplayError(getDisplayError(err));
  };

  const onSearchAccount = (e: React.FormEvent) => {
    e.preventDefault();

    resetAccountSearchQueries();

    try {
      accountSearchRequestSchema.parse(search);
    } catch (err) {
      if (err instanceof ZodError && err.errors.length > 0) {
        setValidationMessages({ ["search"]: [err.errors[0].message] });
        return;
      }
    }

    setValidationMessages(undefined);

    accountSearch({
      search,
    })
      .then((res) =>
        res.accounts.length > 0 && res.accounts.length === 1
          ? setAccountToView(res.accounts[0])
          : undefined,
      )
      .catch((err) =>
        setValidationErrorsAndDisplayGeneralErrors(
          err,
          setValidationMessages,
          onDisplayGeneralErrors,
        ),
      );
  };

  return (
    <form className={styles.searchContainer} onSubmit={onSearchAccount}>
      <h2
        id={labelId}
        className={classNames(styles.heading, styles.searchHeading)}
      >
        {strings.searchByPhone}
      </h2>
      <div className={styles.searchInputContainer}>
        <input
          className={styles.input}
          placeholder={strings.searchByPhonePlaceholder}
          aria-labelledby={labelId}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value.trim());
            setValidationMessages((prev) => ({
              ...prev,
              search: [],
            }));
          }}
        />
        <StyledButton
          type="submit"
          disabled={!search || isDisabled}
          className={styles.searchButton}
          isLoading={isLoading}
        >
          {strings.search}
        </StyledButton>
      </div>
      {validationMessages?.search ? (
        <ValidationMessage validationMessages={validationMessages.search} />
      ) : accountSearchResponse?.message ? (
        <ValidationMessage
          validationMessages={[accountSearchResponse.message]}
        />
      ) : null}
    </form>
  );
}
